import { gsap, Power3, Power0, Back } from 'gsap';
import { CustomEase } from "gsap/dist/CustomEase";
import { SanEngineManager } from '../../engine/SanEngineManager';
import { SanSymbolData } from "../../engine/Symbol/SanSymbolData";
import { RevealerBar } from "./RevealerBar";
import { SanRevealerSymbol } from "../../engine/Symbol/SanRevealerSymbol";
import { BLEND_MODES } from "pixi.js";
import { MultSequence } from "../../proto/MultSequence";
import { SanQuentin2 } from "../../SanQuentin2";
import { StateHandler } from "../../../core/utils/StateHandler";
import { Sequence } from '../../../core/displayobject/Sequence';
import { Helper } from '../../../core/utils/Helper';
import { BasicSymbolType } from '../../../core/reel/symbol/config/IReelSymbolStruct';
import { ReelSymbolStates } from '../../../core/reel/symbol/ReelSpriteSymbolArea';
import { GameConfig } from '../../config/base/GameConfig';
import { RevealSpinner } from './RevealSpinner';
import { SpinePlayer } from '../../../core/displayobject/SpinePlayer';

export class ReavealerState extends StateHandler {
    public static readonly NORMAL: string = "normal";
    public static readonly EXPANDING: string = "expandig";
    public static readonly SPLITTER: string = "split";
    public static readonly EXTRA_FS: string = "extra_fs";
}

export class Revealer extends PIXI.Container {

    private source_core_path: string = "Blocks/";

    private _state: ReavealerState = new ReavealerState(ReavealerState.NORMAL);

    private seq_bg: Sequence;
    private seq_glow: MultSequence;

    // private animReaveal: Sequence;
    private xWaysFX: Sequence;
    private animReavealSpine: SpinePlayer;

    private nonWinGraphic: PIXI.Graphics;
    private fsSpecial_sp: PIXI.Sprite;

    private symContainer: PIXI.Container = new PIXI.Container();
    private cont_bar: RevealerBar;

    private winSymbol: string;
    private resMysterySym: string;

    private id: number = 0;

    private _isActive: boolean = false;
    private _expanded: boolean = false;
    private _revealed: boolean = false;
    private _isTop: boolean = false;

    private hideAlpha: number = 0.2;

    private _loopTl: gsap.core.Timeline;
    private _animTl: gsap.core.Timeline;
    private _idleTl: gsap.core.Timeline | null;
    private _attentionTl: gsap.core.Timeline | null;

    public revSpinner: RevealSpinner;

    public get isTop(): boolean {
        return this._isTop;
    }
    
    public get revealType(): string {
        return this.winSymbol;
    }

    public get bgItem(): PIXI.DisplayObject {
        return this.seq_bg;
    }

    public get isActive(): boolean {
        return this._isActive;
    }

    public get isRevealed(): boolean {
        return this._revealed;
    }

    public get hasStopped():boolean{
        return this._loopTl && !this._loopTl.isActive() && this.isActive;
    }

    public set isRevealed(value: boolean) {
        this._revealed = value;
        this.RePaint();
    }

    private get bgSizeY(): number {
        return Math.round(this.seq_bg.height);
    }

    public get ID(): number {
        return this.id;
    }

    public get isExpanded(): boolean {
        return this._expanded;
    }

    public get state(): ReavealerState {
        return this._state;
    }

    public get animTl(): gsap.core.Timeline {
        return this._animTl ? this._animTl : gsap.timeline();
    }

    private get currentSymCont(): PIXI.Container {
        return this.symContainer;
    }

    private get playWinForXways(): boolean {
        return this.isExpanded && !!this.currentSymCont.alpha;
    }

    private get symChildren(): SanRevealerSymbol[] {
        return <SanRevealerSymbol[]>this.currentSymCont.children;
    }

    public get restShuffleTime(): number {
        if (!this.revSpinner.shuffleTL || !this.revSpinner.shuffleTL.isActive()) return 0;
        return this.revSpinner.shuffleTL.duration() - this.revSpinner.shuffleTL.time();
    }

    constructor(_id: number, _isTop: boolean) {
        super();
        this._isTop = _isTop;
        this.id = _id;
        this.zIndex = 5 - this.id;
        this.initAnimations();
    }

    private fsSymbol: { sym: string, val: number } = { sym: "", val: 0 };
    public setFSSymbol(sym: string, val: number): void {
        this.fsSymbol.sym = sym == "W" ? "" : sym;
        this.fsSymbol.val = val;
    };

    protected initAnimations(): void {

        this.seq_bg = this.createSeq(SanQuentin2.assetLoader.getTextures(this.source_core_path + (this._isTop ? "Block_Top" : "Block_Bottom")));
        this.seq_bg.height = SanSymbolData.fetchSymHeightSize(2);

        this.fillCointainers();
        this.symContainer.alpha = 0;

        // Helping out the animation.
        const halfSymbolOffset:number = (this.isTop ?  -1 : 1) * SanSymbolData.fetchSymHeightSize(2)/2; 
        this.symContainer.pivot.y = halfSymbolOffset;
        this.symContainer.position.y  = halfSymbolOffset;

        this.revSpinner = this.addChild(new RevealSpinner());
        this.revSpinner.alpha = !SanQuentin2.apiPlugin.gameClientConfiguration.showNearMissGfx ? 0 : 0.2;

        this.cont_bar = this.addChild(new RevealerBar(this._isTop ? "Block_Top_Bars" : "Block_Bottom_Bars"));

        this.seq_glow = new MultSequence([
            "Blink_Bar_Tiny",
            "Blink_Bar_Small",
            "Blink_Bar_Medium",
            "Blink_Bar_High",
        ])

        this.seq_glow.anchor.set(0.5, 0.5);
        this.seq_glow.alpha = 0;
        this.seq_glow.blendMode = BLEND_MODES.ADD;
        this.addChild(this.seq_glow);

        this.animReavealSpine = this.addChild(new SpinePlayer("X_ways"));
        this.xWaysFX = this.addChild(new Sequence("XWays_Effects"));
        this.xWaysFX.anchor.set(0.5);
        this.xWaysFX.scale.set(1,3);
        this.xWaysFX.alpha = 0;
        this.xWaysFX.blendMode = BLEND_MODES.ADD;
        // this.animReaveal.alpha = 1;
        // this.animReaveal.scale.set(2);
        // this.animReaveal.visible = false;
        this.animReavealSpine.y += this._isTop ? SanSymbolData.fetchSymHeightSize(4) : -SanSymbolData.fetchSymHeightSize(4);

        // this.animReaveal = this.createSeq(SanQuentin2.assetLoader.getTextures("XpandSingle"));
        // this.animReaveal.alpha = 1;
        // this.animReaveal.scale.set(2);
        // this.animReaveal.visible = false;
        // this.animReaveal.y += this._isTop ? SanSymbolData.fetchSymHeightSize(4) : -SanSymbolData.fetchSymHeightSize(4);

        this.fsSpecial_sp = new PIXI.Sprite(SanQuentin2.assetLoader.getTextures("Blink_Splitter")[0])
        this.fsSpecial_sp.anchor.set(0.5, 1);
        this.fsSpecial_sp.blendMode = BLEND_MODES.ADD;
        this.fsSpecial_sp.visible = false;
        this.addChild(this.fsSpecial_sp);

        this.nonWinGraphic = this.addChild(this.createGraphic());

        this.sortChildren();
    }

    public showRevSpinner(val: boolean): void {
        this.revSpinner.visible = val;
    }

    protected fillCointainers(): void {
        const stepY: number = SanSymbolData.DEFAULT_DIM.HEIGHT * 0.5;
        let posY: number = this._isTop ? 0 : -stepY * 4;
        for (let i: number = 0; i < 5; i++) {
            const sym: SanRevealerSymbol = new SanRevealerSymbol(this.symContainer.children.length, "M1", "normal", 2);
            sym.position.y = posY;
            posY += stepY;
            this.symContainer.addChild(sym);
        }

        this.addChild(this.symContainer);
    }

    protected changeAllContSym(symName: string): void {
        (<SanRevealerSymbol[]>this.symContainer.children).forEach((s: SanRevealerSymbol) => s.setSymbol(symName));
    }

    public setSpecial(sym: string, val: number): void {
        this.revSpinner.setSpecial(sym,val);
        (<SanRevealerSymbol[]>this.symContainer.children).forEach((s: SanRevealerSymbol) => {
            if(s.symName == sym){
                s.isSpecial(val,0.01)
            }
        })
    }

    public setResult(res: string, mystSym: string, isFS: boolean = false): void {

        this.hideAlpha = isFS ? 0.35 : 0.2;

        if (!SanQuentin2.apiPlugin.gameClientConfiguration.showNearMissGfx) {
            this.hideAlpha = 0;
        }
        
        this.revSpinner.playBounceSoundSlice = res == "XS";

        if(this.winAnimTl && this.winAnimTl.isActive()) {
            this.winAnimTl.progress(1);
            this.winAnimTl.kill();
        }
        if(this._idleTl && this._idleTl.isActive()) {
            this._idleTl.progress(1);
            this._idleTl.kill();
        }

        if (res != "XS") {
            if (SanSymbolData.isNormalSymbol(res!) || res === BasicSymbolType.WILD) {
                this._state.set(ReavealerState.NORMAL);
                this.winSymbol = res;
            } else {
                if (isFS && !isNaN(parseFloat(res))) {
                    this.state.set(ReavealerState.EXTRA_FS);
                    return;
                }
                this.resMysterySym = mystSym == "" || mystSym == undefined ? "M" + (1 + Math.floor(Math.random() * 4)) : mystSym;
                this.winSymbol = this.resMysterySym;
                this.changeAllContSym(this.resMysterySym);

                this._state.set(ReavealerState.EXPANDING);
            }
        } else {
            this._state.set(ReavealerState.SPLITTER);
        }

        this.revSpinner.setResultTexture(res);
    }

    public cleanStuff():void{
        this.symChildren.forEach(s => s.cleanUp());
        this.revSpinner.normalSym.cleanUp();
    }

    public specialBarLand(type: number = 0): void {
        this.fsSpecial_sp.texture = SanQuentin2.assetLoader.getTextures(type ? "Blink_Xways" : "Blink_Splitter")[0];
        gsap.fromTo(this.fsSpecial_sp, { alpha: 1 }, { alpha: 0, duration: 0.5 });
    }

    public flashOnce(): gsap.core.Timeline {
        // this.seq_glow.setTexture(SanQuentin2.assetLoader.getTextures("Blink_Bar"));
        const seq =SanQuentin2.overlay.playSequence(this,SanQuentin2.assetLoader.getTextures("Blink_Bar"));
        const tl: gsap.core.Timeline = seq.timeline;
        (seq.item as Sequence).blendMode = BLEND_MODES.ADD;
        tl.add(gsap.to(seq.item,  { alpha: 0, duration:tl.duration() * 0.8 }), "-=" + tl.duration() * 0.2);
        return tl;
    }

    public flashBeforeStop(): gsap.core.Timeline {
        
        const seq = SanQuentin2.overlay.playSequence(this,SanQuentin2.assetLoader.getTextures("Blink_Bar"));
        const tl: gsap.core.Timeline = seq.timeline;
        (seq.item as Sequence).blendMode = BLEND_MODES.ADD;
        const flashDurr: number = 0.5;
        tl.to(seq.item, { alpha: 0, duration:flashDurr }, 0);
        tl.fromTo(seq.item.scale, { x: 1.3, y: 1.3 }, { x: 1, y: 1, duration:flashDurr }, 0);
        return tl;
    }

    public revealRes(playGlow: boolean = true): gsap.core.Timeline {
        this.shuffle(false);

        if (playGlow) {
            this.seq_glow.reset(true);
            this._loopTl.add([
                gsap.to(this.seq_glow, 0.25, { alpha: 0 }),
                gsap.to(this.seq_glow.scale, 0.25, { x: 1.3, y: 1.3 })
            ], 0)
            this._loopTl.add(() => { this.seq_glow.scale.set(1, 1) })
        }

        return this._loopTl;
    }

    public expandRes(playAnim:boolean = true): gsap.core.Timeline {
        this._animTl = Helper.setTimeline(this._animTl);
        if (!this.isExpanded || this._revealed) return this._animTl;
        this._revealed = true;
        if(playAnim){
            this._animTl.add([
                this.animReavealSpine.play({animation:"reveal"}),            
                () => this.revSpinner.revealRes(0.1),
            ], 0);
            this._animTl.to(this.xWaysFX,{duration:this.animReavealSpine.fetchDuration("reveal")/2, alpha:0});
            this._animTl.add(() => {this.xWaysFX.stop(true)});
        }else{
            this._animTl.add(() => {
                this.animReavealSpine.stop();
                this.animReavealSpine.visible = false;
                this.revSpinner.revealRes(0.1);
            },0)
            this._animTl.to(this.xWaysFX,{ duration:this.animReavealSpine.fetchDuration("reveal")/2, alpha:0},0);
            this._animTl.add(() => {this.xWaysFX.stop(true); })
        }

        return this._animTl;
    }

    public setBounceSound(val: boolean) {
        this.revSpinner.playBounceSound = val;
    }

    public openBar(withGlow?:boolean): gsap.core.Timeline {
        if (this._isActive) return gsap.timeline();
        this._isActive = true;
        this.revSpinner.playBounceSound = true;
        if(withGlow) this.revSpinner.useOverlayGlow(true);
            
        return this.cont_bar.open();
    }

    public closeBar(): gsap.core.Timeline {
        if (!this._isActive) return gsap.timeline();
        this.revSpinner.useOverlayGlow(false);
        this._revealed = false;
        this._isActive = false;
        this.revSpinner.playBounceSound = false;
        const tl: gsap.core.Timeline = gsap.timeline();

        tl.add([
            () => { this.cont_bar.close() },
            gsap.to(this.revSpinner, 0.25, { alpha: this.hideAlpha }),
        ]);

        if (this.seq_glow.timeline && this.seq_glow.timeline?.isActive()) {
            this.seq_glow.reset(true);
            this.seq_glow.play().to(this.seq_glow, 0.3, { alpha: 0 });
        }

        tl.play();
        return tl;
    }

    public shuffle(active: boolean): gsap.core.Timeline {
        this._loopTl = this.revSpinner.shuffle(active, () => { this.cleanStuff() });

        if (!active) gsap.to(this.revSpinner, GameConfig.REEL_ENGINE_CONFIG.DURATION.STOP, { alpha: this.isActive ? 1 : this.hideAlpha })
        return this._loopTl;
    }

    public didDoubleConnect(){
        this.currentSymCont.alpha = 0;
        this._expanded = true;
        this._revealed = true;
        this.revSpinner.xWaysReset();
    }

    public xWaysReset(){
        this.currentSymCont.alpha = 0;
        this._expanded = false;
        this._revealed = false;
        this.revSpinner.xWaysReset();
    }

    public expand(expands: boolean, ignoreReel: boolean = false): gsap.core.Timeline {
        if (this._expanded === expands) return Helper.setTimeline();

        this._expanded = expands;
        this._revealed = false;

        const durrExp: number = GameConfig.REEL_ENGINE_CONFIG.DURATION.EXPAND!;
        const easeing: any = GameConfig.REEL_ENGINE_CONFIG.EASING.EXPAND;

        const tl: gsap.core.Timeline = this.revSpinner.expand(expands);

        if (expands) {
            this.revSpinner.prepareForExpand(this.winSymbol == "W", this._isTop, this.resMysterySym);
            this.xWaysFX.position = this.animReavealSpine.position;
            tl.add(() => { this.xWaysFX.play({repeat:-1}); this.xWaysFX.alpha = 1;},0);
            // tl.fromTo(this.xWaysFX, { alpha: 0 }, {
            //     alpha:  1,
            //     ease: Back.easeIn as any,
            //     duration: durrExp/2
            // }, 0);
            tl.fromTo(this.xWaysFX.scale, { x:0.9, y: 2.8 }, {
                y:  3,
                x:  1,
                ease: Back.easeIn as any,
                duration: durrExp/2
            }, 0);
        }

        if (!ignoreReel) {
            tl.add(SanEngineManager.getReel(this.id).onExpandSymbol({
                shrink: expands,
                targetSym: this._isTop ? 2 : 0,
                type: expands ? 2 : 4,
                duration: durrExp
            }),0);
        }

        tl.fromTo(this.currentSymCont.scale, { y: !expands ? 1 : 74/370, }, {
            y: !expands ? 74/370 : 1,
            ease: easeing,
            duration: durrExp
        }, 0);

        tl.fromTo(this.currentSymCont, { alpha: expands ? 0 : 1 }, {
            alpha: expands ? 1 : 0,
            ease: Power3.easeIn as any,
            duration: durrExp
        }, 0);

        return tl;
    }

    private winAnimTl: gsap.core.Timeline;
    public winAnimation(isAWin:boolean,duration: number = 0.5, lvl: number): gsap.core.Timeline {
        this.winAnimTl = Helper.setTimeline(this.winAnimTl,{}, true);
        return this.playWin(isAWin,this.winAnimTl, duration, 0, lvl);
    }

    hintValue(val:number,ways:number) {
        SanQuentin2.overlay.playValueText(this,val,ways);
    }

    public playBigWin(duration: number, lvl: number): gsap.core.Timeline {
        const tls: gsap.core.Timeline = Helper.setTimeline();
        if (this.playWinForXways) {
            for (let i: number = 0; i < this.symChildren.length; i++) 
                tls.add(() => this.symChildren[i].playBigWin(duration,lvl),0);
        }else{
            tls.add(() => this.revSpinner.normalSym.playBigWin(duration,lvl),0);
        }
        return tls;
    }

    public markNonWin(enable: boolean, alpha: number = 0.35, durr:number = 0.25): void {
        const tls: gsap.core.Timeline = Helper.setTimeline();
        if (this.playWinForXways) {
            for (let i: number = 0; i < this.symChildren.length; i++) 
                tls.add(() => {this.symChildren[i].markNonWin(enable,alpha,durr)},0);
        }
        tls.add(() => {this.revSpinner.normalSym.markNonWin(enable,alpha,durr)},0);
    }

    stopWin(duration: number, ignoreDarknessChange?: boolean) {

        if (this.playWinForXways) {
            for (let i: number = 0; i < this.symChildren.length; i++) {
                this.symChildren[i].stopWin(duration);
            }
        }
        else{
            this.revSpinner.normalSym.stopWin(duration);
        }
        if (!ignoreDarknessChange)
            this.markNonWin(false, undefined, duration);
    }

    public stopBigWin(duration: number, lvl: number): gsap.core.Timeline | gsap.core.Timeline {
        const tls: gsap.core.Timeline = Helper.setTimeline();
        if (this.playWinForXways) {
            for (let i: number = 0; i < this.symChildren.length; i++) 
                tls.add(() => this.symChildren[i].stopBigWin(duration),0);
            return tls;
        }
        tls.add(() => this.revSpinner.normalSym.stopBigWin(duration),0);

        return tls;
    }

    private glowLoop(duration: number): gsap.core.Timeline {
        return this.seq_glow!.play({
            duration: duration,
            onUpdate: () => {
                const nt: number = this.seq_glow!.timeline!.progress();
                if (nt > .5) {
                    this.seq_glow!.alpha = 0.4 + (nt * 0.6) * 2;
                } else
                    this.seq_glow!.alpha = 1 - ((nt - 0.5) * 2) * 0.6;
            },
            onCompleteCallback: () => this.glowLoop(duration)
        })
    }

    public glow(duration: number, lvl: number): gsap.core.Timeline {
        if (this._state.equal(ReavealerState.EXPANDING)) {

            const tls: gsap.core.Timeline = Helper.setTimeline();
            // for (let i: number = 0; i < this.symChildren.length; i++) tls.add(() => this.symChildren[i].glow(duration,lvl)),0;
            return tls;
        }
        this.seq_glow.stop();
        return this.seq_glow!.play({
            duration: duration,
            onUpdate: () => this.seq_glow!.alpha = 1 - this.seq_glow!.timeline!.totalProgress()
        }, lvl);
    }

    public glowMult(): gsap.core.Timeline | gsap.core.Timeline[] {
        this.revSpinner.useOverlayGlow(false);
        this.seq_glow.setTexture(SanQuentin2.assetLoader.getTextures("Blink_Bar"));
        const tl: gsap.core.Timeline = this.seq_glow.play({ onStart: () => this.seq_glow.alpha = 1, duration: 0.25 });
        tl.add(gsap.to(this.seq_glow, 0.35 * 0.8, { alpha: 0 }), "-=" + tl.duration() * 0.2);
        return tl;
    }

    PlayIdle(hits: boolean, duration: number): void {
        // if (!this._idleTl) this._idleTl = Helper.setTimeline(this._idleTl!, gsap.timeline(), true);

        if (this.playWinForXways) {
            for (let i: number = 0; i < this.symChildren.length; i++) {
                this.symChildren[i].PlayIdle(hits,duration);
            }
        }else{
            this.revSpinner.normalSym.PlayIdle(hits,duration);
        }
        this.markNonWin(!hits, 0.5, duration/4);
    }

    public setInteractive(val: boolean): void {
        if (!this.isActive) return;

        // if(this._expanded)
        //     this.symChildren.forEach(s => s.setInteractive(val))
        // else if(this._state.equal(ReavealerState.NORMAL))
        // {
        //     if(val)
        //         this.revSpinner.on('pointerdown', () => this.sendToMiniTable());
        //     else
        //         this.revSpinner.off('pointerdown', () => this.sendToMiniTable());
        //     this.revSpinner.interactive = val;
        // }
    }

    private playWin(isAWin:boolean,tl: gsap.core.Timeline, duration: number, startpos: number, lvl: number): gsap.core.Timeline {
        if(!this.isActive) return tl;

        if (this.playWinForXways) {
            for (let i: number = 0; i < this.symChildren.length; i++) {
                this.symChildren[i].playWin(isAWin,duration,lvl);
                tl.to(this,{duration:duration}, startpos);
            }
        }
        else{
            this.revSpinner.normalSym.playWin(isAWin,duration,lvl);
        }
        return tl;
    }

    public resetIdleTimeline(): void {
        if (this._idleTl) this._idleTl.totalProgress(1);
        this._idleTl = Helper.setTimeline(this._idleTl!);
    }

    public onResize(): void {
        this.position = Helper.copyLocalPosition(this, SanEngineManager.getReel(this.id));

        if (this._state.equal(ReavealerState.EXPANDING)) {
            for (let i: number = 0; i < this.symChildren.length; i++) {
                this.symChildren[i].onResize();
            }
        }
        else{
            this.revSpinner.normalSym.onResize();
        }

        this.RePaint();
    }

    public attentionDarkness(alphaValue: number = 0.5, duration: number = 0.2, force?: boolean): gsap.core.Timeline {
        this._attentionTl = Helper.setTimeline(this._attentionTl!);

        if (!force && this._state.equal(ReavealerState.SPLITTER) && this.isActive) {
            return this._attentionTl;
        }

        const toAlpha = alphaValue;
        this.RePaint();
        this._attentionTl.to(this.nonWinGraphic, duration, { alpha: toAlpha });
        return this._attentionTl;
    }

    private createSeq(texture: PIXI.Texture[]): Sequence {
        const seq: Sequence = this.addChild(new Sequence(texture));
        seq.anchor.set(0.5, 0.5);
        return seq;
    }

    private createGraphic(): PIXI.Graphics {
        const graphicBg: PIXI.Graphics = new PIXI.Graphics();
        graphicBg.beginFill(0x000000, 1);
        graphicBg.drawRect(-SanSymbolData.DEFAULT_DIM.WIDTH / 2, -this.bgSizeY / 2,
            SanSymbolData.DEFAULT_DIM.WIDTH, this.bgSizeY);
        graphicBg.endFill();
        graphicBg.alpha = 0;
        return graphicBg;
    }

    private RePaint(): void {
        const w: number = SanSymbolData.DEFAULT_DIM.WIDTH;
        const h: number = this.isExpanded ? this.bgSizeY * 4 : this.bgSizeY;
        const hPos: number = this.isExpanded ? h * 0.75 : 0;
        this.nonWinGraphic.clear();
        this.nonWinGraphic.beginFill(0x000000, 1);
        this.nonWinGraphic.drawRect(-w / 2, -h/2,
            w, h);
        this.nonWinGraphic.endFill();
    }
}